import { CurrentUser } from '@appTypes/CurrentUser';
import { NotificationHeaderResource, NotificationResource } from '@appTypes/index';
import amLogo from '@assets/svgs/am-logo.svg';
import ConfirmationModal from '@components/shared/modals/confirmation-modal/ConfirmationModal';
import Tooltip from '@components/shared/tooltip/Tooltip';
import { Icon } from '@components/style/Icon';
import { PopupBlockedIcon } from '@components/style/PopupBlockedIcon';
import { Typography } from '@components/style/Typography';
import { useCheckDevice } from '@hooks/UseCheckDevice';
import { useNotification } from '@hooks/UseNotification';
import { useNotificationBulkPrint } from '@hooks/useNotificationBulkPrint';
import { useOutsideClickDetection } from '@hooks/UseOutsideClickDetection';
import { useUpdatedWindowSize } from '@hooks/UseUpdatedWindowSize';
import { AMCustomToast } from '@services/utils/ToastUtils';
import { AppState } from '@store/AppState';
import { INotificationsState } from '@store/notifications/NotificationReducer';
import { theme } from '@theme';
import { FC, useLayoutEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { NotificationHeaderType } from '../../enums/NotificationType';
import NotificationList from './notification-list/NotificationList';
import {
  BadgeItem,
  BottomActions,
  CloseIcon,
  ExportDealsheets,
  GlobalActions,
  MarkAsRead,
  MobileRefreshNotifications,
  NotifBadge,
  NotificationBar,
  NotificationContainerWrapper,
  NotificationsHead,
  NotificationsLabel,
  ReadUnreadExportContainer,
  StyledLink,
  TabNav,
  TabNavNotifBadge,
  VisibilityToggle,
} from './NotificationContainerStyles';
import { RealTimeNotification } from './RealTimeNotification';

const NotificationContainer: FC = () => {
  const [, windowHeight] = useUpdatedWindowSize();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const { isMobileDevice } = useCheckDevice();
  const { storeCode } = useSelector<AppState, CurrentUser>(
    (currentUser) => currentUser.currentUser,
  );
  const { loading, items, filteredItems, unreadFilteredItems, isExportNotification } = useSelector<
    AppState,
    INotificationsState
  >((notificationState) => notificationState.notification);
  const {
    isVisible,
    setIsVisible,
    onNotificationClick,
    onRead,
    setActiveFilter,
    activeFilter,
    fetchNotifications,
    showReadNts,
    setShowReadNts,
    markAllNotifsRead,
    headers,
    notificationBuckets,
    markType,
    isEssentialType,
    isIncentiveAdvantageType,
    getMobileFilteredItems,
  } = useNotification();

  const { bulkPrintOffer, isBulkPrintVisible, bulkPrintNotifications, getNotifTooltipFromFilter } =
    useNotificationBulkPrint();
  const [isPrintOfferModalOpen, setIsPrintOfferModalOpen] = useState<boolean>(false);

  const handleOutsideClick = (isVisible: boolean) => {
    setIsVisible(isVisible);
  };

  const handleOnClick = (item: NotificationResource) => {
    onNotificationClick(item);
  };

  useOutsideClickDetection(wrapperRef, handleOutsideClick);

  const fetchNotificationsFromApi = (event?: React.MouseEvent<HTMLElement>): void => {
    fetchNotifications();
    if (event) {
      event.stopPropagation();
    }
  };

  const unreadNotifications = () => {
    if (isMobileDevice) {
      return getMobileFilteredItems()?.filter((x) => !x.isRead)?.length;
    }
    return items.filter((x) => !x.isRead)?.length;
  };

  const onNotificationIconClick = (event: React.MouseEvent<HTMLElement>) => {
    setIsVisible(!isVisible);
    // event.stopPropagation(); TODO
  };

  const onBulkPrint = (event: React.MouseEvent<HTMLElement>) => {
    openPrintOfferModal();
    event.stopPropagation();
  };

  const toggleReadNotifications = (event: React.MouseEvent<HTMLElement>) => {
    setShowReadNts(!showReadNts);
    event.stopPropagation();
  };

  const markAllAsRead = (event: React.MouseEvent<HTMLElement>) => {
    markAllNotifsRead(activeFilter);
    event.stopPropagation();
  };

  const openPrintOfferModal = (): void => {
    setIsPrintOfferModalOpen(true);
  };

  const handleCancel = () => {
    setIsPrintOfferModalOpen(false);
  };

  const bodyComponent: JSX.Element = (
    <div className='row'>
      <div className='col-sm-2'>
        <NotificationLogo src={amLogo} />
      </div>
      <div className='col-sm-10'>
        <Typography size={theme.typography.h4} fontWeight={theme.typography.fontWeights.medium}>
          You are about to print {bulkPrintNotifications.length} deal sheet
          {bulkPrintNotifications.length > 1 ? 's' : ''} from the{' '}
          {getNotifTooltipFromFilter(activeFilter)} category. When complete, a new tab will open in
          your browser.
        </Typography>
      </div>
    </div>
  );

  const getPopupBlockedToastMessage = (): JSX.Element => {
    return (
      <>
        <Typography size={theme.typography.h5} fontWeight={theme.typography.fontWeights.normal}>
          Your selected deal sheets are being prepared; this may take up to a minute. Pop-ups must
          be enabled in the Address Bar
        </Typography>
        <PopupBlockedIcon />
      </>
    );
  };

  const handleSubmit = (): void => {
    AMCustomToast(getPopupBlockedToastMessage(), false, true);
    setIsPrintOfferModalOpen(false);
    bulkPrintOffer();
  };

  const visibleHeaders = headers.filter((x) => x.isVisible);
  const showScroll = isMobileDevice ? visibleHeaders.length > 6 : visibleHeaders.length > 11;
  const containerRef = useRef<HTMLDivElement>(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el || !showScroll) return;

    const checkScroll = () => {
      if (!el) return;
      const { scrollLeft, scrollWidth, clientWidth } = el;
      setShowLeftArrow(scrollLeft > 5);
      setShowRightArrow(scrollLeft + clientWidth < scrollWidth - 5);
    };

    // Resize and orientation handling
    const handleResize = () => {
      requestAnimationFrame(checkScroll);
    };

    checkScroll();
    el.addEventListener('scroll', checkScroll);
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);
    return () => {
      el.removeEventListener('scroll', checkScroll);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, [showScroll, headers, isVisible]);

  const scroll = (direction: 'left' | 'right') => {
    if (containerRef.current) {
      const scrollAmount = direction === 'left' ? -150 : 150;
      containerRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  return (
    <div ref={wrapperRef}>
      <BadgeItem
        data-testid='badge-item'
        onClick={onNotificationIconClick}
        isMobileDevice={isMobileDevice}
      >
        <i className='am-icon am-icon-bell'></i>
        {unreadNotifications() > 0 && !loading && (
          <NotifBadge isMobileDevice={isMobileDevice}>{unreadNotifications()}</NotifBadge>
        )}
      </BadgeItem>
      {isVisible && !loading && (
        <NotificationContainerWrapper>
          <NotificationBar isMobileDevice={isMobileDevice} data-testid='notification-bar'>
            <NotificationsHead data-testid='notifications-head'>
              <NotificationsLabel data-testid='notifications-label'>
                Notifications
              </NotificationsLabel>
              <CloseIcon data-testid='close'>
                <i
                  className='am-icon am-icon-x'
                  data-testid='am-icon-x-close'
                  onClick={() => setIsVisible(false)}
                ></i>
              </CloseIcon>
              {notificationBuckets &&
                notificationBuckets[activeFilter] > 0 &&
                markType &&
                !isIncentiveAdvantageType &&
                !isMobileDevice && (
                  <GlobalActions data-testid='global-actions'>
                    <MarkAsRead data-testid='mark-as-read' onClick={markAllAsRead}>
                      Mark {markType} as Read
                    </MarkAsRead>
                  </GlobalActions>
                )}
              <TabNav isMobileDevice={isMobileDevice} showScroll={showScroll} data-testid='tab-nav'>
                <div className='scroll-tabs-wrapper' data-testid='tab-scroll'>
                  {showLeftArrow && showScroll && (
                    <button className='am-icon-angle-left' onClick={() => scroll('left')}></button>
                  )}
                  <div className='scroll-container'>
                    <div className='scroll-wrapper' ref={containerRef}>
                      {visibleHeaders.map((header: NotificationHeaderResource) => (
                        <li
                          key={header.key}
                          className={activeFilter === header.key ? 'active' : ''}
                          onClick={() => setActiveFilter(header.key)}
                        >
                          {header.hasIcon ? (
                            <Tooltip
                              trigger={
                                <>
                                  <Icon type={header.icon} additionalClassName='am-icon' />
                                  {notificationBuckets && notificationBuckets[header.key] > 0 && (
                                    <TabNavNotifBadge isMobileDevice={isMobileDevice}>
                                      {notificationBuckets[header.key]}
                                    </TabNavNotifBadge>
                                  )}
                                </>
                              }
                              content={<span>{header.label}</span>}
                            />
                          ) : (
                            <span>{header.label}</span>
                          )}
                        </li>
                      ))}
                    </div>
                  </div>
                  {showRightArrow && showScroll && (
                    <button
                      className='am-icon-angle-right'
                      data-testid='right-scroll'
                      onClick={() => scroll('right')}
                    ></button>
                  )}
                </div>
              </TabNav>
              {filteredItems && filteredItems.length > 0 && (
                <ReadUnreadExportContainer data-testid='read-unread-export-container'>
                  <VisibilityToggle data-testid='visibility-toggle' isMobileDevice={isMobileDevice}>
                    <span onClick={toggleReadNotifications}>
                      <i
                        className={`am-icon ${showReadNts ? 'am-icon-eye-full-crossed' : 'am-icon am-icon-eye-full'}`}
                      ></i>{' '}
                      {showReadNts ? 'Hide' : 'Show'} Read Notifications
                    </span>
                  </VisibilityToggle>
                  {!isMobileDevice ? (
                    <ExportDealsheets data-testid='export-dealsheets'>
                      {activeFilter !== NotificationHeaderType.All &&
                        isBulkPrintVisible &&
                        activeFilter !== NotificationHeaderType.PrivateIncentive &&
                        !isExportNotification &&
                        !isIncentiveAdvantageType && (
                          <span
                            className='export-dealsheets-link'
                            data-testid='export-dealsheets-link'
                            onClick={onBulkPrint}
                          >
                            <i className='am-icon am-icon-export'></i> Print Unread Sheets (25 max)
                          </span>
                        )}
                    </ExportDealsheets>
                  ) : (
                    <MobileRefreshNotifications
                      data-testid='mobile-refresh-notifications'
                      isMobileDevice={isMobileDevice}
                    >
                      <Icon
                        type='refresh'
                        additionalClassName='am-icon'
                        size={theme.typography.h1}
                        onClick={fetchNotificationsFromApi}
                      />
                    </MobileRefreshNotifications>
                  )}
                </ReadUnreadExportContainer>
              )}
            </NotificationsHead>
            <NotificationList
              windowHeight={windowHeight}
              notifications={unreadFilteredItems}
              isEssentialType={isEssentialType}
              onClick={handleOnClick}
              onRead={onRead}
              filteredItems={filteredItems}
              activeFilter={activeFilter}
            />
            {!isMobileDevice && (
              <BottomActions data-testid='bottom-actions'>
                <StyledLink onClick={fetchNotificationsFromApi}>Refresh Notifications</StyledLink>
              </BottomActions>
            )}
          </NotificationBar>
        </NotificationContainerWrapper>
      )}
      <RealTimeNotification
        activeFilter={activeFilter}
        storeCode={storeCode}
        onClick={handleOnClick}
        isNotificationWindowOpen={isVisible}
        notifications={items}
      />
      <ConfirmationModal
        isOpen={isPrintOfferModalOpen}
        handleSubmitClick={handleSubmit}
        handleCancelClick={handleCancel}
        cancelButtonText='Cancel'
        submitButtonText='Download for Print'
        title='Bulk Deal Sheet Print'
        bodyComponent={bodyComponent}
        showCloseIcon={true}
        padding={'20px 20px 20px 15px'}
        minHeight={'225px'}
        minWidth={'465px'}
        submitButtonBackgroundColor={theme.palette.purple}
        submitButtonTextColor={theme.palette.white}
        submitButtonDisabled={false}
        width={'490px'}
      />
    </div>
  );
};

export default NotificationContainer;

const NotificationLogo = styled.img`
  margin-top: 15px;
  width: 46px;
  height: 28px;
`;
